import { Eye, EyeOff } from 'lucide-react'
import * as React from 'react'

import { cn } from '@/utils'

interface LegendItem {
  key: string
  label: string
  color: string
  visible?: boolean
}

interface ChartLegendProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onToggle'> {
  items?: LegendItem[]
  onToggle?: (key: string) => void
  className?: string
  position?: 'right' | 'bottom' | 'top'
  maxHeight?: string
  interactive?: boolean
  chartMargins?: { top: number; bottom: number }
}

const ChartLegend = React.memo(
  React.forwardRef<HTMLDivElement, ChartLegendProps>(
    (
      {
        items = [],
        onToggle,
        className,
        position = 'right',
        maxHeight,
        interactive = true,
        chartMargins,
        ...props
      },
      ref,
    ) => {
      const calculatedStyle = React.useMemo(() => {
        const style: React.CSSProperties = {}

        if (position === 'right' && chartMargins) {
          style.top = `${chartMargins.top}px`
          const availableHeight = chartMargins.bottom
            ? `calc(100% - ${chartMargins.top}px - ${chartMargins.bottom}px)`
            : `calc(100% - ${chartMargins.top}px)`
          style.maxHeight = maxHeight || availableHeight
        } else if (maxHeight) {
          style.maxHeight = maxHeight
        }

        return style
      }, [position, chartMargins, maxHeight])

      if (!items.length) return null

      const positionClasses = {
        right: 'absolute right-2',
        bottom: 'absolute bottom-2 left-1/2 -translate-x-1/2',
        top: 'absolute top-2 left-1/2 -translate-x-1/2',
      }

      const containerClasses = {
        right: 'flex-col min-w-[180px] max-w-[200px]',
        bottom: 'flex-row flex-wrap justify-center max-w-full',
        top: 'flex-row flex-wrap justify-center max-w-full',
      }

      return (
        <div
          ref={ref}
          className={cn(
            'bg-background/95 backdrop-blur-sm border rounded-md overflow-hidden flex flex-col',
            positionClasses[position],
            className,
          )}
          style={calculatedStyle}
          {...props}
        >
          <div
            className={cn(
              'flex gap-2 p-2 overflow-y-auto',
              containerClasses[position],
            )}
          >
            {items.map((item, i) => (
              <button
                type="button"
                key={item.key || i}
                onClick={() => interactive && onToggle?.(item.key)}
                disabled={!interactive}
                className={cn(
                  'flex items-center gap-2 px-2 py-1 rounded-sm text-sm transition-all group min-w-0 justify-start',
                  position === 'right' && 'w-full',
                  interactive && 'hover:bg-accent cursor-pointer',
                  !interactive && 'cursor-default',
                  item.visible === false && 'opacity-40',
                )}
              >
                <div
                  className="w-3 h-3 rounded-full shrink-0"
                  style={{ backgroundColor: item.color }}
                />
                <span
                  className="text-xs text-muted-foreground truncate min-w-0 flex-1 text-left"
                  title={item.label}
                >
                  {item.label}
                </span>
                {interactive && item.visible !== false && (
                  <Eye className="h-3 w-3 shrink-0 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                )}
                {interactive && item.visible === false && (
                  <EyeOff className="h-3 w-3 shrink-0 text-muted-foreground" />
                )}
              </button>
            ))}
          </div>
        </div>
      )
    },
  ),
)
ChartLegend.displayName = 'ChartLegend'

export { ChartLegend }
